<script setup lang="ts">
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSourceStore } from '@/stores/SourceStore.ts';
import { SubscriptionTier } from '@/types/SubscriptionTier.ts';
import { useUserInfoStore } from '@/stores/UserInfoStore.ts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { startSubscriptionProcessAction } from '@/actions/app/StartSubscriptionProcessAction.ts';
import { computed, onMounted, onUnmounted } from 'vue';
import NavHeader from '@/components/menu/NavHeader.vue';
import { HouseIcon, TriangleAlertIcon, XIcon } from 'lucide-vue-next';
import VariantCard from '@/components/index/VariantCard.vue';
import { Hotkey } from '@/types/Hotkey.ts';
import { router, ROUTES } from '@/router.ts';
import HotkeyIndicator from '@/components/hotkey/HotkeyIndicator.vue';
import { useClientStore } from '@/stores/ClientStore.ts';
import { getNeighbouringStepConfig } from '@/utils/PromptUtils.ts';
import { eventEmitter, type HotkeyPressPayload } from '@/lib/eventEmitter.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { StepConfigService } from '@/services/config/StepConfigService.ts';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useSettingsStore } from '@/stores/SettingsStore.ts';
import { getOs } from '@/utils/RandomUtils.ts';

const hostStore = useSourceStore();
const userInfoStore = useUserInfoStore();
const clientStore = useClientStore();
const sessionStore = useSessionStore();
const settingsStore = useSettingsStore();

const startSubscriptionProcess = () => {
    startSubscriptionProcessAction();
};

const getRunPath = (stepConfigId: StepConfig['id'] | undefined): string => {
    if (!clientStore.activeSessionId || !clientStore.activeProfileId) {
        return router.resolve({
            name: ROUTES.errorNotFound,
        }).href;
    }

    let stepConfig: StepConfig | undefined;
    if (stepConfigId) {
        stepConfig = StepConfigService.getStepConfig(stepConfigId);
    } else {
        stepConfig = getNeighbouringStepConfig(clientStore.activeProfileId, undefined, 'next');
    }

    if (!stepConfig) {
        return router.resolve({
            name: ROUTES.errorNotFound,
        }).href;
    }

    return router.resolve({
        name: ROUTES.runIndex,
        params: {
            sessionId: clientStore.activeSessionId,
            profileConfigId: clientStore.activeProfileId,
            stepConfigId: stepConfig.id,
            variableChainHash: '_',
        },
    }).href;
};

const hideZoomMacOsAnnouncement = () => {
    settingsStore.zoomMacOsAnnouncementHidden = true;
};

const onHotkeyPress = (payload: HotkeyPressPayload) => {
    if (payload.hotkey === Hotkey.Right) {
        router.push(getRunPath(undefined));
    }
};

const stepConfigs = computed(() => {
    const allStepConfigs = StepConfigService.getAllStepConfigs();

    return Object.values(allStepConfigs)
        .filter((p) => p.profileId === clientStore.activeProfileId)
        .sort((a, b) => a.order - b.order);
});

onMounted(() => {
    eventEmitter.addListener('hotkeyPress', onHotkeyPress);
});

onUnmounted(() => {
    eventEmitter.removeListener('hotkeyPress', onHotkeyPress);
});
</script>

<template>
    <NavHeader>
        <Breadcrumb>
            <BreadcrumbList>
                <BreadcrumbItem>
                    <BreadcrumbPage class="flex items-center gap-2">
                        <HouseIcon class="size-4" />
                        Home
                    </BreadcrumbPage>
                </BreadcrumbItem>
                <template v-if="clientStore.activeSessionId && clientStore.activeProfileId">
                    <template v-for="(stepConfig, index) in stepConfigs" :key="stepConfig.id">
                        <BreadcrumbSeparator v-if="index !== stepConfigs.length" />
                        <BreadcrumbItem>
                            <BreadcrumbLink as-child>
                                <HotkeyIndicator :hotkey="Hotkey.Right" v-if="index === 0" />
                                <RouterLink :to="getRunPath(stepConfig.id)">{{
                                    stepConfig.name
                                }}</RouterLink>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                    </template>
                </template>
            </BreadcrumbList>
        </Breadcrumb>
    </NavHeader>

    <div class="flex flex-1 flex-col gap-4 p-4 pt-0">
        <!-- MacOS warning -->
        <Alert
            class="border border-red-600"
            v-if="
                !settingsStore.zoomMacOsAnnouncementHidden &&
                !sessionStore.isInsideWebView &&
                getOs() === 'macos'
            "
        >
            <TriangleAlertIcon class="h-4 w-4" />
            <AlertTitle class="mb-2 flex items-center justify-between">
                <span> Warning for MacOS users (Zoom)</span>
                <XIcon
                    class="size-4 text-muted-foreground hover:text-muted-foreground/80"
                    @click="hideZoomMacOsAnnouncement"
                />
            </AlertTitle>
            <AlertDescription>
                <span class="text-sm text-muted-foreground">
                    If you are using Leetcode Wizard with newer versions of Zoom on MacOS, you might
                    encounter an issue where the app is visible while screen sharing. To fix this
                    issue, you will need to set "Screen capture mode" to "Advanced capture with
                    window filtering" in your Zoom settings (Share screen tab) -
                    <a
                        href="https://leetcodewizard.io/help/detection#zoom-macos"
                        class="text-blue-600 hover:underline"
                        target="_blank"
                    >
                        More info
                    </a>
                </span>
            </AlertDescription>
        </Alert>

        <!-- Web view card-->
        <VariantCard v-if="sessionStore.isInsideWebView" :variant="'checked'" :color="'green'">
            <CardHeader>
                <CardTitle class="flex items-center text-lg text-green-700"> Web view </CardTitle>
            </CardHeader>
            <CardContent class="text-sm">
                All output from the desktop app will become visible here after you start a new
                session. The web view can only be controlled from the desktop app (or from the
                device it is running on with the global hotkeys).
                <div class="mt-4">
                    If you are using the web view during an interview, make sure to hide the desktop
                    app (Settings -> Hide app) or by pressing the configured hide hotkey.
                </div>
            </CardContent>
        </VariantCard>

        <!-- Trial subscription card -->
        <VariantCard
            v-if="userInfoStore.subscriptionTier === SubscriptionTier.Trial"
            variant="unchecked"
            :color="'yellow'"
        >
            <CardHeader>
                <CardTitle class="flex items-center text-lg text-yellow-700">
                    Trial subscription
                </CardTitle>
            </CardHeader>
            <CardContent class="text-sm">
                You have
                <span class="font-bold">{{
                    Math.max(0, userInfoStore.creditsRemaining).toLocaleString(undefined, {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                    })
                }}</span>
                trial credits remaining. Each
                <TooltipProvider :delay-duration="100">
                    <Tooltip>
                        <TooltipTrigger><span class="underline">action</span></TooltipTrigger>
                        <TooltipContent :side="'bottom'">
                            An action can be defined as: requesting algorithms, code, tests, or a
                            complexity analysis.
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                costs on average 1 trial credit but this can differ depending on input and output
                size.
                <div class="mt-4 text-muted-foreground">
                    The trial mode of Leetcode Wizard only works on
                    <a href="https://leetcode.com/problemset/" target="_blank" class="underline"
                        >LeetCode.com</a
                    >. Our Pro version works on all input sources. Click
                    <a href="javascript:void(0)" class="underline" @click="startSubscriptionProcess"
                        >here</a
                    >
                    to subscribe.
                </div>
            </CardContent>
        </VariantCard>

        <!-- Pro subscription card -->
        <VariantCard
            v-if="userInfoStore.subscriptionTier === SubscriptionTier.Pro"
            variant="checked"
            :color="'green'"
        >
            <CardHeader>
                <CardTitle class="flex items-center text-lg text-green-700">
                    Pro subscription
                </CardTitle>
            </CardHeader>
            <CardContent class="text-sm"> You have an active subscription. </CardContent>
        </VariantCard>

        <!-- Input source card-->
        <VariantCard
            :variant="hostStore.selectedSourceId ? 'checked' : 'unchecked'"
            :color="hostStore.selectedSourceId ? 'green' : 'red'"
        >
            <CardHeader>
                <CardTitle
                    class="flex items-center text-lg"
                    :class="{
                        'text-green-700': hostStore.selectedSourceId,
                        'text-red-700': !hostStore.selectedSourceId,
                    }"
                >
                    <span v-if="hostStore.selectedSourceId">Input source selected</span>
                    <span v-else>No input source selected</span>
                </CardTitle>
            </CardHeader>
            <CardContent class="text-sm">
                <div v-if="hostStore.selectedSourceId" class="text-md">
                    Selected: <span class="font-semibold">{{ hostStore.selectedSourceName }}</span
                    >.
                </div>
                <div v-else>No input source selected.</div>
                <div class="mt-4 text-muted-foreground">
                    <span v-if="!sessionStore.isInsideWebView">
                        You can change the input source from the settings menu (Settings -> Input
                        source).</span
                    >
                    <span v-else>
                        You can change the input source from the settings menu inside the desktop
                        app.</span
                    >
                </div>
                <div class="mt-4 text-muted-foreground">
                    Please make sure the Leetcode problem description and initial code are fully
                    visible in the selected input source before pressing solve to guarantee the best
                    possible results.
                </div>
            </CardContent>
        </VariantCard>
    </div>
</template>
